import { useCallback, useEffect, useState } from 'react';
import { Loader2, CalendarClock } from 'lucide-react';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { extractErrorMessage } from '@/lib/errorMessage';
import { DemoBookingCard, DemoBooking } from './DemoBookingCard';

// ────────────────────────────────────────────────────────────────────────────
// Super-admin demo bookings — list + full lifecycle (complete / no-show /
// cancel / reschedule) via the shared DemoBookingCard, which records every
// status change (with an optional remark) on the booking timeline and syncs
// the linked prospect on the backend. Mirrors the vendor Meetings tab.
// ────────────────────────────────────────────────────────────────────────────

export function SuperAdminDemoBookings({ adminId: _adminId }: { adminId: string }) {
  const [scope, setScope] = useState<'upcoming' | 'past'>('upcoming');
  const [items, setItems] = useState<DemoBooking[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await saApi.get('/super-admin/demo/bookings', { params: { scope } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast('error', extractErrorMessage(e, 'Failed to load bookings'));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="flex items-center gap-2 p-3 border-b border-slate-100">
        {(['upcoming', 'past'] as const).map(s => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={`px-3 h-8 rounded-lg text-xs font-semibold capitalize ${scope === s ? 'bg-[#6366F1] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center">
          <CalendarClock className="h-8 w-8 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No {scope} demos</p>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {items.map(b => (
            <DemoBookingCard key={b.id} booking={b} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}
